import { ChevronDownIcon } from "@solomon-labs/design/components/icons";
import { Text } from "@solomon-labs/design/components/ui";
import React, { useState } from "react";

import { ListItem } from "../../../components/Data/List";
import { Button } from "../../../components/ui/button";

import { IconButton } from "./IconButton";
import { PolicyConfiguration } from "./PolicyConfiguration";
import {
    newUserDescription,
    parseNewUserConfig,
    parseTranches,
    parseWalletConfig,
    walletDescription
} from "./policyFixtures";
import { TrancheSummaryRow } from "./TrancheSummaryRow";
import type { Policy } from "./types";

/** Sim-only forced-state seam for a row's Remove / Edit affordances. */
export interface PolicyRowDemo {
    /** Force the Remove button's hover/press visual. */
    removePhase?: "hover" | "press" | null;
    /** Force the Edit button's hover/press visual. */
    editPhase?: "hover" | "press" | null;
    /** Force the whole ROW's hover treatment (the `ListItem` accent line +
     *  fill). Paired with `editPhase` in the read-view tour so the cursor
     *  paints the row highlight AND the Edit hover together while it rests on
     *  the row. */
    rowHover?: boolean;
}

export interface PolicyRowProps {
    policy: Policy;
    value?: number | string;
    /** When provided (draft only), the row shows a "Remove" affordance that
     *  detaches the policy from the program. */
    onRemove?: () => void;
    /** When provided (active / ended only), the row shows a per-policy "Edit"
     *  affordance that lets the user change this policy's value in place. */
    onChangeValue?: (value: number | string) => void;
    /** Sim-only — force hover/press on the Remove / Edit affordances. */
    demo?: PolicyRowDemo;
    /** Sim-only cursor key placed on the Remove button. */
    removeCursorKey?: string;
    /** Sim-only cursor key placed on the Edit button. */
    editCursorKey?: string;
}

/**
 * PolicyRow — a policy attached to a program, rendered as one row of the
 * Policies list. A Domain Component that wraps the `ListItem` GC: the policy
 * title + active description fill the body; the detail value + Remove/Edit fill
 * the `trailing` slot. List-style policies (tranches, wallets) expand into an
 * accordion below the row; scalar policies just show their value.
 */
export function PolicyRow({
    policy,
    value,
    onRemove,
    onChangeValue,
    demo,
    removeCursorKey,
    editCursorKey
}: PolicyRowProps): React.ReactElement {
    const [expanded, setExpanded] = useState(false);
    const [editing, setEditing] = useState(false);
    const [draft, setDraft] = useState<number | string>(value ?? "");
    const canEditValue =
        onChangeValue !== null &&
        onChangeValue !== undefined &&
        policy.kind !== "summary";
    const startEdit = (): void => {
        setDraft(value ?? "");
        setExpanded(false);
        setEditing(true);
    };
    const saveEdit = (): void => {
        onChangeValue?.(draft);
        setEditing(false);
    };

    const valueText =
        policy.kind === "slider"
            ? policy.describe(Number(value))
            : policy.kind === "wallet"
              ? walletDescription(policy, value)
              : policy.kind === "new-user"
                ? newUserDescription(policy, value)
                : policy.description;
    const selectLabel =
        policy.kind === "select"
            ? (policy.options.find((o) => o.value === String(value))?.label ??
              String(value))
            : null;
    const sliderValue =
        policy.kind === "slider"
            ? `${Number(value).toLocaleString("en-US")}${
                  policy.unit === "usdpt" ? " USDv" : ""
              }`
            : null;
    const walletConfig =
        policy.kind === "wallet"
            ? parseWalletConfig(value, policy.modes?.[0]?.value ?? "")
            : null;
    const newUserBonus =
        policy.kind === "new-user"
            ? `${(
                  parseNewUserConfig(value).bonusAmount ??
                  policy.defaultBonusAmount
              ).toLocaleString("en-US")} USDv`
            : null;
    const tranches = policy.kind === "tranche" ? parseTranches(value) : null;
    const trailingLabel =
        selectLabel ??
        sliderValue ??
        newUserBonus ??
        (walletConfig !== null
            ? `${walletConfig.addresses.length} wallet${
                  walletConfig.addresses.length === 1 ? "" : "s"
              }`
            : tranches !== null
              ? `${tranches.length} tranche${tranches.length === 1 ? "" : "s"}`
              : null);

    // Policies with list-style detail (tranches, wallet addresses) expand into
    // an accordion; simple scalar policies just show their value.
    const expandable = policy.kind === "tranche" || policy.kind === "wallet";

    return (
        <>
            <ListItem
                itemKey={policy.id}
                align="start"
                padding="px-4 py-3"
                hoverLineColor="rgb(var(--color-line-tint-base))"
                forceHover={demo?.rowHover === true}
                onClick={
                    !editing && expandable
                        ? (): void => setExpanded((e) => !e)
                        : undefined
                }
                trailing={
                    <div className="flex flex-col items-end gap-1 shrink-0">
                        {/* Line 1 — value + expand chevron + (draft) remove,
                            aligned with the policy title. */}
                        <div className="flex items-center gap-5">
                            {!editing && trailingLabel !== null && (
                                <Text
                                    variant="small"
                                    render={<span />}
                                    className="text-foreground-base font-numeric"
                                >
                                    {trailingLabel}
                                </Text>
                            )}
                            {!editing && expandable && (
                                <ChevronDownIcon
                                    className={`size-4 text-foreground-neutral transition-transform duration-200 ${
                                        expanded ? "rotate-180" : ""
                                    }`}
                                />
                            )}
                            {onRemove && (
                                <IconButton
                                    data-sim-cursor-key={removeCursorKey}
                                    forceHover={demo?.removePhase === "hover"}
                                    forcePressed={demo?.removePhase === "press"}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        onRemove();
                                    }}
                                >
                                    <Text variant="small" render={<span />}>
                                        Remove
                                    </Text>
                                </IconButton>
                            )}
                        </div>
                        {/* Line 2 — per-policy edit toggle, aligned with the
                            policy description. */}
                        {canEditValue && (
                            <IconButton
                                tone="ember"
                                interaction="underline"
                                data-sim-cursor-key={editCursorKey}
                                forceHover={demo?.editPhase === "hover"}
                                forcePressed={demo?.editPhase === "press"}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    if (editing) setEditing(false);
                                    else startEdit();
                                }}
                            >
                                <Text variant="small" render={<span />}>
                                    {editing ? "Cancel" : "Edit"}
                                </Text>
                            </IconButton>
                        )}
                    </div>
                }
            >
                <div className="min-w-0">
                    <Text
                        variant="h3"
                        render={<span />}
                        className="text-foreground-base block"
                    >
                        {policy.title}
                    </Text>
                    <Text
                        variant="fine-print"
                        className="text-foreground-muted mt-1"
                    >
                        {valueText}
                    </Text>
                </div>
            </ListItem>
            {editing && policy.kind !== "summary" && (
                <div className="px-4 py-4 bg-surface-sunken flex flex-col gap-3">
                    <PolicyConfiguration
                        policy={policy}
                        value={draft}
                        onChange={setDraft}
                    />
                    <div>
                        <Button
                            variant="raised"
                            interaction="lifted"
                            size="sm"
                            onClick={saveEdit}
                        >
                            Save
                        </Button>
                    </div>
                </div>
            )}
            {!editing && expandable && expanded && (
                <div className="px-4 py-3 bg-surface-sunken flex flex-col gap-1.5">
                    {policy.kind === "tranche" &&
                        ((tranches ?? []).length === 0 ? (
                            <Text
                                variant="fine-print"
                                className="text-foreground-muted"
                            >
                                No tranches configured.
                            </Text>
                        ) : (
                            (tranches ?? []).map((t, i) => (
                                <TrancheSummaryRow
                                    key={i}
                                    label={
                                        policy.amountUnit === "usdpt"
                                            ? `≥ ${(t.amount ?? 0).toLocaleString("en-US")} USDv`
                                            : `≥ ${(t.amount ?? 0).toLocaleString("en-US")} days`
                                    }
                                    value={`${t.multiplier ?? 0}x Multiplier`}
                                />
                            ))
                        ))}
                    {policy.kind === "wallet" &&
                        ((walletConfig?.addresses ?? []).length === 0 ? (
                            <Text
                                variant="fine-print"
                                className="text-foreground-muted"
                            >
                                No wallets added.
                            </Text>
                        ) : (
                            (walletConfig?.addresses ?? []).map((addr) => (
                                <Text
                                    key={addr}
                                    variant="fine-print"
                                    render={<span />}
                                    className="text-foreground-neutral font-numeric truncate"
                                >
                                    {addr}
                                </Text>
                            ))
                        ))}
                </div>
            )}
        </>
    );
}
